import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetTeamQuery as GetTeam } from '__generated__/graphql';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import ContactPicker, { Participant } from '../Contacts/ContactPicker';
import EditBackendTeamMutation from './EditBackendTeamMutation';
import GetTeamTypesQuery from './GetTeamTypesQuery';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useValidation from '../../../common/useValidation';

type Team = GetTeam['team'];

const editTeamSchema = yup.object({
  title: yup.string().min(1).max(100),
});

export interface EditTeamFormProps {
  team: Team;
  onSuccess?: () => void;
}

const EditTeamForm = ({ team, onSuccess }: EditTeamFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();

  const [captain, setCaptain] = useState<Participant>(team.captain);

  const form = useForm({
    values: {
      title: team.title,
      team_type: { id: team.team_type.id },
      captain: { id: team.captain.id },
    },
  });

  const { data, loading: loadingTeamTypes } = useQuery(GetTeamTypesQuery,
    {
      variables: {
        project_id: project.id,
        event_id: team.event.id,
      },
    },
  );

  const teamTypes = data?.project.event.team_types || [];

  const handleCaptainChange = (participant: Participant) => {
    form.set('captain.id', participant.id);
    setCaptain(participant);
  };

  const [editTeam, { error, loading }] = useMutation(EditBackendTeamMutation, {
    variables: {
      input: {
        id: team.id,
        ...form.values,
      },
    },
    onCompleted: () => {
      onSuccess?.();
      notifier.success(t('notifications.team_edited'));
    },
    onError: () => {
      //
    },
  });

  const { errors, valid } = useValidation({
    schema: editTeamSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editTeam()}>
      <UI.FormGrid>
        <UI.H4>{t('edit_team')}</UI.H4>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="TeamName">
            {t('team_name')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.DebouncedInput {...form.text('title')} maxLength={100} id="TeamName" />
          <UI.ErrorMessages attribute={t('team_name')} errors={errors.title} />
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.team_type, touched: form.touched.team_type, relative: true }}>
          <UI.InputLabel htmlFor="EditTeamTeamType">
            {t('team_type')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          {loadingTeamTypes && (
            <UI.InputFallback />
          )}
          {!loadingTeamTypes && (
            <UI.Select
              {...form.select(
                'team_type.id',
                teamTypes.map((teamType) => teamType.id),
              )}
              id="EditTeamTeamType"
            >
              {teamTypes.map((teamTypes, index) => (
                <option {...form.option(index)} key={index}>
                  {teamTypes.title || t('team')}
                </option>
              ))}
            </UI.Select>
          )}
        </UI.InputGroup>

        <UI.InputGroup sc={{ valid: !errors.captain, touched: form.touched.captain }}>
          <UI.InputLabel>
            {t('team_captain')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <ContactPicker
            participant={captain}
            onChange={handleCaptainChange}
            nullable={false}
          />
          <UI.ErrorMessages attribute={t('event_title')} errors={errors.captain} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_team')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTeamForm;
